import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CalendarClock, Plus, Trash2, X, Save, Repeat, AlertTriangle, CheckCircle2, Moon, Sun, UserCircle2 } from "lucide-react";
import { fmtDateTime } from "../lib/datetime";

const WINDOWS = [
  { value: "day", label: "Day", icon: Sun },
  { value: "night", label: "Night", icon: Moon },
];

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Rota() {
  const { user } = useAuth();
  const canManage = ["admin", "contracts_manager"].includes(user?.role);
  const [shifts, setShifts] = useState([]);
  const [current, setCurrent] = useState(null);
  const [users, setUsers] = useState([]);
  const [operatives, setOperatives] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showPattern, setShowPattern] = useState(false);

  const load = async () => {
    try {
      const [r, c, p] = await Promise.all([
        api.get("/rota"),
        api.get("/rota/current"),
        api.get("/people"),
      ]);
      setShifts(r.data);
      setCurrent(c.data);
      const peopleList = p.data || [];
      const usersDerived = peopleList
        .filter((x) => x.linked_user_id)
        .map((x) => ({ id: x.linked_user_id, name: x.full_name, role: x.access_level || "" }));
      setUsers(usersDerived);
      setOperatives(peopleList.map((x) => ({ id: x.id, name: x.full_name, op_type: x.op_type })));
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const sortedShifts = useMemo(() => {
    return [...shifts].sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
  }, [shifts]);

  const remove = async (id) => {
    if (!window.confirm("Delete this shift?")) return;
    try {
      await api.delete(`/rota/${id}`);
      setSuccess("Shift deleted");
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="rota-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 20 }}>
        <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>NEWELL RESPONSE SYSTEM · ON-CALL ROTA</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>On-call rota</h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>Schedule of who is on call — day &amp; night. The NRS cascade reads this to decide who to alert when a callout fires.</div>
      </header>

      <CurrentBanner current={current} />

      {error ? <div data-testid="rota-error" className="card" style={{ padding: 12, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", gap: 8, alignItems: "center" }}><AlertTriangle size={16} /> {error} <button className="btn-ghost" onClick={() => setError("")} style={{ marginLeft: "auto" }}><X size={12} /></button></div> : null}
      {success ? <div data-testid="rota-success" className="card" style={{ padding: 12, marginBottom: 12, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", gap: 8, alignItems: "center" }}><CheckCircle2 size={16} /> {success} <button className="btn-ghost" onClick={() => setSuccess("")} style={{ marginLeft: "auto" }}><X size={12} /></button></div> : null}

      {canManage ? (
        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <button data-testid="rota-new-shift" className="btn-primary" onClick={() => setShowNew(true)}><Plus size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />New shift</button>
          <button data-testid="rota-generate-pattern" className="btn-ghost" onClick={() => setShowPattern(true)}><Repeat size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />Generate from pattern</button>
        </div>
      ) : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="rota-table">
          <thead>
            <tr>
              <th>Start</th>
              <th>End</th>
              <th>Window</th>
              <th>Primary</th>
              <th>Backup</th>
              <th>Paired labourer</th>
              <th>Source</th>
              {canManage ? <th style={{ textAlign: "right" }}>Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {sortedShifts.length === 0 ? (
              <tr><td colSpan={canManage ? 8 : 7} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No shifts scheduled. Click &quot;New shift&quot; or use &quot;Generate from pattern&quot;.</td></tr>
            ) : sortedShifts.map((s) => (
              <tr key={s.id} data-testid={`rota-row-${s.id}`}>
                <td data-label="Start" className="mono" style={{ fontSize: 12 }}>{fmtDateTime(s.start_at)}</td>
                <td data-label="End" className="mono" style={{ fontSize: 12 }}>{fmtDateTime(s.end_at)}</td>
                <td data-label="Window"><WindowBadge w={s.window_type} /></td>
                <td data-label="Primary" style={{ fontWeight: 600 }}>{s.primary?.name || <span style={{ color: "#dc2626" }}>missing</span>}</td>
                <td data-label="Backup">{s.backup?.name || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                <td data-label="Paired labourer">{s.paired_labourer?.full_name || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                <td data-label="Source"><span className="mono" style={{ fontSize: 10, color: "#64748b" }}>{s.source}{s.pattern_week ? ` · ${s.pattern_week}` : ""}</span></td>
                {canManage ? (
                  <td data-label="Actions" style={{ textAlign: "right" }}>
                    <button data-testid={`rota-delete-${s.id}`} className="btn-ghost" onClick={() => remove(s.id)} style={{ color: "#b91c1c" }}><Trash2 size={12} /></button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew ? (
        <NewShiftModal
          users={users}
          operatives={operatives}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); setSuccess("Shift created"); load(); }}
          onError={(m) => setError(m)}
        />
      ) : null}
      {showPattern ? (
        <PatternModal
          users={users}
          operatives={operatives}
          onClose={() => setShowPattern(false)}
          onSaved={(n) => { setShowPattern(false); setSuccess(`Generated ${n} shifts`); load(); }}
          onError={(m) => setError(m)}
        />
      ) : null}
    </div>
  );
}

function CurrentBanner({ current }) {
  const shift = current?.shift;
  if (!shift) {
    return (
      <div data-testid="current-onCall-empty" className="card" style={{ padding: 16, marginBottom: 16, background: "#fef3c7", borderLeft: "4px solid #f59e0b", display: "flex", gap: 12, alignItems: "center" }}>
        <CalendarClock size={20} color="#92400e" />
        <div>
          <div style={{ fontWeight: 900, color: "#92400e" }}>No on-call shift covers right now.</div>
          <div style={{ fontSize: 12, color: "#92400e", marginTop: 2 }}>If a callout comes in, it will fall back to admin/contracts manager.</div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="current-onCall-banner" className="card" style={{ padding: 16, marginBottom: 16, background: "#0f172a", color: "#fff", borderLeft: "4px solid #ff5a00", display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 14, alignItems: "center" }}>
      <UserCircle2 size={28} color="#ff5a00" />
      <div>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#94a3b8" }}>ON CALL NOW · {shift.window_type === "night" ? "NIGHT" : "DAY"}</div>
        <div style={{ fontWeight: 900, fontSize: 18 }} data-testid="current-onCall-name">{shift.primary?.name || "(missing primary)"}</div>
        <div style={{ fontSize: 12, color: "#cbd5e1" }}>
          {shift.backup?.name ? `Backup: ${shift.backup.name}` : "No backup set"}
          {shift.paired_labourer?.full_name ? ` · Paired: ${shift.paired_labourer.full_name}` : ""}
        </div>
      </div>
      <div className="mono" style={{ fontSize: 11, color: "#94a3b8", textAlign: "right" }}>
        until<br />
        <span style={{ color: "white" }}>{fmtDateTime(shift.end_at)}</span>
      </div>
    </div>
  );
}

function WindowBadge({ w }) {
  const isNight = w === "night";
  return (
    <span className="mono" style={{ background: isNight ? "#0f172a" : "#fef3c7", color: isNight ? "white" : "#92400e", padding: "2px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>
      {isNight ? "NIGHT" : "DAY"}
    </span>
  );
}

function localToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

function NewShiftModal({ users, operatives, onClose, onSaved, onError }) {
  const [form, setForm] = useState({
    start_at: "",
    end_at: "",
    window_type: "day",
    primary_user_id: "",
    backup_user_id: "",
    paired_labourer_id: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/rota", {
        ...form,
        start_at: localToIso(form.start_at),
        end_at: localToIso(form.end_at),
        backup_user_id: form.backup_user_id || null,
        paired_labourer_id: form.paired_labourer_id || null,
      });
      onSaved();
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="New on-call shift" onClose={onClose}>
      <form onSubmit={submit}>
        <Row2>
          <Field label="Start *"><input data-testid="shift-start" required type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></Field>
          <Field label="End *"><input data-testid="shift-end" required type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></Field>
        </Row2>
        <Field label="Window">
          <div style={{ display: "flex", gap: 8 }}>
            {WINDOWS.map(({ value, label, icon: Icon }) => (
              <button key={value} type="button" data-testid={`shift-window-${value}`} onClick={() => setForm({ ...form, window_type: value })} className={form.window_type === value ? "btn-primary" : "btn-ghost"} style={{ flex: 1 }}>
                <Icon size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Primary on-call *">
          <select data-testid="shift-primary" required value={form.primary_user_id} onChange={(e) => setForm({ ...form, primary_user_id: e.target.value })}>
            <option value="">— select —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} {u.role ? `· ${u.role}` : ""}</option>)}
          </select>
        </Field>
        <Field label="Backup on-call">
          <select data-testid="shift-backup" value={form.backup_user_id} onChange={(e) => setForm({ ...form, backup_user_id: e.target.value })}>
            <option value="">— none —</option>
            {users.filter((u) => u.id !== form.primary_user_id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        <Field label="Paired labourer (joins gang on dispatch)">
          <select data-testid="shift-paired" value={form.paired_labourer_id} onChange={(e) => setForm({ ...form, paired_labourer_id: e.target.value })}>
            <option value="">— none —</option>
            {operatives.map((o) => <option key={o.id} value={o.id}>{o.name} {o.op_type ? `· ${o.op_type}` : ""}</option>)}
          </select>
        </Field>
        <Field label="Notes"><input data-testid="shift-notes" type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" data-testid="shift-save" className="btn-primary" disabled={busy}><Save size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{busy ? "Saving…" : "Save shift"}</button>
        </div>
      </form>
    </Modal>
  );
}

function PatternModal({ users, operatives, onClose, onSaved, onError }) {
  const today = new Date();
  // default to next Monday
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const isoDate = monday.toISOString().slice(0, 10);

  const blankEntry = () => ({
    day_of_week: 0, window_type: "day", primary_user_id: "", backup_user_id: "", paired_labourer_id: "", start_hour: 8, start_minute: 0, end_hour: 18, end_minute: 0,
  });

  const [weekStart, setWeekStart] = useState(isoDate);
  const [weeks, setWeeks] = useState(4);
  const [entries, setEntries] = useState([blankEntry()]);
  const [busy, setBusy] = useState(false);

  const addEntry = () => setEntries([...entries, blankEntry()]);
  const removeEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));
  const updEntry = (i, k, v) => setEntries(entries.map((e, idx) => idx === i ? { ...e, [k]: v } : e));

  const submit = async (e) => {
    e.preventDefault();
    if (entries.some((x) => !x.primary_user_id)) { onError("Each pattern row needs a primary on-call user."); return; }
    setBusy(true);
    try {
      const cleaned = entries.map((x) => ({ ...x, backup_user_id: x.backup_user_id || null, paired_labourer_id: x.paired_labourer_id || null }));
      const { data } = await api.post("/rota/generate", { week_start: weekStart, weeks: Number(weeks), pattern_a: cleaned, pattern_b: [] });
      onSaved(data.created);
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Generate rota from weekly pattern" onClose={onClose} wide>
      <form onSubmit={submit}>
        <Row2>
          <Field label="Week starts (Monday) *"><input type="date" data-testid="pattern-week-start" required value={weekStart} onChange={(e) => setWeekStart(e.target.value)} /></Field>
          <Field label="Number of weeks *"><input type="number" data-testid="pattern-weeks" min={1} max={26} required value={weeks} onChange={(e) => setWeeks(e.target.value)} /></Field>
        </Row2>
        <div className="mono" style={{ fontSize: 11, color: "#64748b", marginTop: 12, marginBottom: 6 }}>WEEKLY ROWS · ONE PER DAY/WINDOW</div>
        <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 4 }}>
          {entries.map((e, i) => (
            <div key={i} style={{ padding: 10, borderBottom: i < entries.length - 1 ? "1px solid #f1f5f9" : "none", display: "grid", gridTemplateColumns: "60px 60px 1fr 1fr 80px 80px 30px", gap: 6, alignItems: "center" }}>
              <select data-testid={`pat-day-${i}`} value={e.day_of_week} onChange={(ev) => updEntry(i, "day_of_week", Number(ev.target.value))}>
                {DAY_NAMES.map((d, idx) => <option key={d} value={idx}>{d}</option>)}
              </select>
              <select data-testid={`pat-window-${i}`} value={e.window_type} onChange={(ev) => updEntry(i, "window_type", ev.target.value)}>
                <option value="day">Day</option><option value="night">Night</option>
              </select>
              <select data-testid={`pat-primary-${i}`} value={e.primary_user_id} onChange={(ev) => updEntry(i, "primary_user_id", ev.target.value)}>
                <option value="">— primary —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <select data-testid={`pat-backup-${i}`} value={e.backup_user_id} onChange={(ev) => updEntry(i, "backup_user_id", ev.target.value)}>
                <option value="">— backup —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <input type="number" min={0} max={23} value={e.start_hour} onChange={(ev) => updEntry(i, "start_hour", Number(ev.target.value))} title="Start hour (UTC)" />
              <input type="number" min={0} max={23} value={e.end_hour} onChange={(ev) => updEntry(i, "end_hour", Number(ev.target.value))} title="End hour (UTC)" />
              <button type="button" className="btn-ghost" onClick={() => removeEntry(i)} style={{ color: "#b91c1c" }}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <button type="button" data-testid="pattern-add-row" className="btn-ghost" onClick={addEntry} style={{ marginTop: 8 }}><Plus size={12} style={{ marginRight: 4 }} />Add row</button>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" data-testid="pattern-generate" className="btn-primary" disabled={busy}><Repeat size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{busy ? "Generating…" : "Generate shifts"}</button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, children, onClose, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "white", maxWidth: wide ? 760 : 520, width: "100%", padding: 24, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontWeight: 900 }}>{title}</h2>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Row2({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
